import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart3, Loader2, AlertCircle, FileText, Download, Calendar,
  Plus, Trash2, Clock, Mail, Folder, Settings, X, CheckCircle2,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import type { Questionnaire, PaginatedResponse } from '../types';

interface ReportConfig {
  id: string; name: string; organizationId: string; questionnaireId?: string;
  questionnaireName?: string; dataFieldIds: string[]; format: string;
  schedule?: string; outputPath?: string; emailRecipients?: string;
  emailSubject?: string; includeCharts?: boolean; createdAt: string;
}

const formatLabels: Record<string, string> = { CSV: 'CSV', XLSX: 'Excel', PDF: 'PDF' };

export default function ReportsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const orgId = user?.organizationId || '';

  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [editConfigId, setEditConfigId] = useState<string | null>(null);
  const [showConfigList, setShowConfigList] = useState(false);

  const [cfgForm, setCfgForm] = useState({
    name: '', format: 'CSV', schedule: '', outputPath: '',
    emailRecipients: '', emailSubject: '', includeCharts: false,
    questionnaireId: '', dataFieldIds: [] as string[],
  });

  // ─── Queries ───
  const { data: rawQData } = useQuery<PaginatedResponse<Questionnaire>>({
    queryKey: ['questionnaires'],
    queryFn: async () => {
      const res = await api.get('/questionnaires', { params: { limit: 100 } });
      return res.data;
    },
  });
  const questionnaires: Questionnaire[] = Array.isArray(rawQData) ? rawQData : (rawQData?.data ?? []);

  const { data: configs } = useQuery<ReportConfig[]>({
    queryKey: ['report-configs'],
    queryFn: async () => {
      const res = await api.get('/reports/configs', { params: { organizationId: orgId } });
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
  });

  // ─── Mutations ───
  const saveConfigMut = useMutation({
    mutationFn: async () => {
      const payload = {
        ...cfgForm,
        organizationId: orgId,
        emailRecipients: cfgForm.emailRecipients ? JSON.stringify(cfgForm.emailRecipients.split(',').map(s => s.trim())) : null,
        dataFieldIds: cfgForm.dataFieldIds,
      };
      if (editConfigId) return api.patch(`/reports/configs/${editConfigId}`, payload);
      return api.post('/reports/configs', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-configs'] });
      setShowConfigForm(false); setEditConfigId(null);
      setCfgForm({ name: '', format: 'CSV', schedule: '', outputPath: '', emailRecipients: '', emailSubject: '', includeCharts: false, questionnaireId: '', dataFieldIds: [] });
    },
  });

  const deleteConfigMut = useMutation({
    mutationFn: async (id: string) => api.delete(`/reports/configs/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['report-configs'] }),
  });

  // ─── Download ───
  const handleGenerate = async (format: string) => {
    if (!selectedQuestionnaireId) return;
    setIsGenerating(true); setError(null); setReportUrl(null);
    try {
      const res = await api.get(`/reports/questionnaire/${selectedQuestionnaireId}`, {
        params: { format }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      setReportUrl(url);
      const a = document.createElement('a'); a.href = url;
      a.download = `bericht.${format.toLowerCase()}`; a.click();
    } catch { setError('Bericht konnte nicht generiert werden'); }
    finally { setIsGenerating(false); }
  };

  const openEdit = (cfg: ReportConfig) => {
    setEditConfigId(cfg.id);
    setCfgForm({
      name: cfg.name, format: cfg.format, schedule: cfg.schedule || '',
      outputPath: cfg.outputPath || '', includeCharts: !!cfg.includeCharts,
      emailRecipients: (() => { try { return JSON.parse(cfg.emailRecipients || '[]').join(', '); } catch { return cfg.emailRecipients || ''; } })(),
      emailSubject: cfg.emailSubject || '',
      questionnaireId: cfg.questionnaireId || '', dataFieldIds: cfg.dataFieldIds || [],
    });
    setShowConfigForm(true);
  };

  const parsedRecipients = (r: string | undefined) => {
    try { return JSON.parse(r || '[]').join(', '); } catch { return r || '—'; }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <BarChart3 size={28} className="text-primary" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Berichte</h1>
          <p className="text-sm text-gray-500">Berichte generieren, planen und exportieren</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Configuration */}
        <div className="lg:col-span-2 space-y-4">
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Berichtskonfiguration</h2>

            <div className="space-y-4">
              <div>
                <label className="label">Fragebogen auswählen</label>
                <select className="input-field" value={selectedQuestionnaireId}
                  onChange={(e) => { setSelectedQuestionnaireId(e.target.value); setReportUrl(null); }}>
                  <option value="">Bitte wählen</option>
                  {questionnaires.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
                </select>
              </div>

              {selectedQuestionnaireId && (
                <div>
                  <label className="label">Exportformat</label>
                  <div className="grid grid-cols-3 gap-3">
                    <button onClick={() => handleGenerate('csv')} disabled={isGenerating}
                      className="btn-secondary py-3 flex-col items-center gap-1 h-auto">
                      <FileText size={24} className="text-accent-green-dark" />
                      <span className="text-xs font-medium">CSV</span>
                    </button>
                    <button onClick={() => handleGenerate('xlsx')} disabled={isGenerating}
                      className="btn-secondary py-3 flex-col items-center gap-1 h-auto">
                      <FileText size={24} className="text-accent-blue" />
                      <span className="text-xs font-medium">Excel</span>
                    </button>
                    <button onClick={() => handleGenerate('pdf')} disabled={isGenerating}
                      className="btn-secondary py-3 flex-col items-center gap-1 h-auto">
                      <FileText size={24} className="text-primary" />
                      <span className="text-xs font-medium">PDF</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {isGenerating && (
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-4">
                <Loader2 size={16} className="animate-spin" /> Bericht wird generiert...
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mt-4">
                <AlertCircle size={18} className="shrink-0" /><span>{error}</span>
              </div>
            )}
            {reportUrl && (
              <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700 mt-4">
                <CheckCircle2 size={18} className="shrink-0" /><span>Bericht heruntergeladen.</span>
              </div>
            )}
          </div>
        </div>

        {/* Right: Sidebar */}
        <div className="space-y-4">
          {/* Geplante Berichte */}
          <div className="card bg-accent-blue/5 border-accent-blue/20">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Calendar size={22} className="text-accent-blue" />
                <h3 className="font-semibold text-gray-900">Geplante Berichte</h3>
              </div>
              <button onClick={() => { setEditConfigId(null); setCfgForm({ name: '', format: 'CSV', schedule: '', outputPath: '', emailRecipients: '', emailSubject: '', includeCharts: false, questionnaireId: '', dataFieldIds: [] }); setShowConfigForm(true); }}
                className="p-1 text-accent-blue hover:bg-accent-blue/10 rounded" title="Neue Planung">
                <Plus size={16} />
              </button>
            </div>

            {(configs || []).length === 0 ? (
              <p className="text-sm text-gray-500">Keine geplanten Berichte. Klicke + um einen anzulegen.</p>
            ) : (
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {(configs || []).map((cfg) => (
                  <div key={cfg.id} className="flex items-center justify-between text-sm bg-white/60 rounded px-2 py-1.5 group hover:bg-white">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-800 truncate">{cfg.name}</p>
                      <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
                        <span className="badge-info text-[9px]">{cfg.format}</span>
                        {cfg.schedule && <span className="font-mono">{cfg.schedule}</span>}
                        {cfg.outputPath && <span><Folder size={9} /> {cfg.outputPath}</span>}
                        {cfg.emailRecipients && <span><Mail size={9} /> {parsedRecipients(cfg.emailRecipients)}</span>}
                      </div>
                    </div>
                    <div className="flex opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openEdit(cfg)} className="p-1 text-gray-400 hover:text-blue-600"><Settings size={12} /></button>
                      <button onClick={() => deleteConfigMut.mutate(cfg.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Config Form Modal */}
            {showConfigForm && (
              <div className="mt-3 p-3 bg-white rounded-lg border border-accent-blue/30 space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold">{editConfigId ? 'Planung bearbeiten' : 'Neue Planung'}</h4>
                  <button onClick={() => setShowConfigForm(false)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
                </div>
                <input className="input-field text-sm" placeholder="Name" value={cfgForm.name}
                  onChange={e => setCfgForm({...cfgForm, name: e.target.value})} />
                <div className="grid grid-cols-2 gap-2">
                  <select className="input-field text-xs" value={cfgForm.format}
                    onChange={e => setCfgForm({...cfgForm, format: e.target.value})}>
                    {Object.entries(formatLabels).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <select className="input-field text-xs" value={cfgForm.questionnaireId}
                    onChange={e => setCfgForm({...cfgForm, questionnaireId: e.target.value})}>
                    <option value="">— Fragebogen —</option>
                    {questionnaires.map(q => <option key={q.id} value={q.id}>{q.name}</option>)}
                  </select>
                </div>
                <input className="input-field text-xs font-mono" placeholder="Cron (z.B. 0 6 * * 1)" value={cfgForm.schedule}
                  onChange={e => setCfgForm({...cfgForm, schedule: e.target.value})} />
                <input className="input-field text-xs" placeholder="Ausgabe-Pfad (/reports/...)" value={cfgForm.outputPath}
                  onChange={e => setCfgForm({...cfgForm, outputPath: e.target.value})} />
                <input className="input-field text-xs" placeholder="E-Mail Empfänger (kommagetrennt)" value={cfgForm.emailRecipients}
                  onChange={e => setCfgForm({...cfgForm, emailRecipients: e.target.value})} />
                <input className="input-field text-xs" placeholder="E-Mail Betreff" value={cfgForm.emailSubject}
                  onChange={e => setCfgForm({...cfgForm, emailSubject: e.target.value})} />
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={cfgForm.includeCharts}
                    onChange={e => setCfgForm({...cfgForm, includeCharts: e.target.checked})} />
                  Diagramme inkludieren
                </label>
                <div className="flex gap-2">
                  <button onClick={() => saveConfigMut.mutate()} disabled={!cfgForm.name || saveConfigMut.isPending}
                    className="btn-primary text-xs py-1.5 flex-1">
                    {saveConfigMut.isPending ? <Loader2 size={12} className="animate-spin" /> : 'Speichern'}
                  </button>
                  <button onClick={() => setShowConfigForm(false)} className="btn-secondary text-xs py-1.5">Abbrechen</button>
                </div>
              </div>
            )}
          </div>

          {/* Verfügbare Berichte */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-2">Verfügbare Berichte</h3>
            <ul className="space-y-2 text-sm">
              {questionnaires.slice(0, 5).map((q) => (
                <li key={q.id}>
                  <button onClick={() => { setSelectedQuestionnaireId(q.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors w-full text-left">
                    <Download size={14} className="shrink-0" />
                    <span className="truncate">{q.name}</span>
                  </button>
                </li>
              ))}
              {questionnaires.length === 0 && (
                <li className="text-gray-400">Keine Fragebögen verfügbar</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
